import { z } from "zod";

import { CURRENCIES } from "./currencies";
import { VINYL_GRADES } from "./listTypes";
import { localeSchema } from "./localeSchema";
import { usernameSchema } from "./username";

const currencyCodes = CURRENCIES.map((currency) => currency.code) as [
  (typeof CURRENCIES)[number]["code"],
  ...(typeof CURRENCIES)[number]["code"][],
];

export const currencyCodeSchema = z
  .string()
  .trim()
  .transform((value) => value.toUpperCase())
  .pipe(z.enum(currencyCodes));
export const countryCodeSchema = optionalTrimmedString(2).refine(
  (value) => value === undefined || value.length === 2,
  "Country must be a two-letter code",
);
export const vinylGradeSchema = z.enum(VINYL_GRADES);

export const profileDisplayNameSchema = trimmedRequiredString(1, 100);
export const profileBioSchema = optionalTrimmedString(160);
export const profileCitySchema = optionalTrimmedString(100);
export const optionalImageUrlSchema = z
  .union([z.string().trim().min(1).max(2048), z.literal("").transform(() => undefined), z.null()])
  .optional();
export const sellerEmailSchema = z
  .union([z.string().trim().email().max(320), z.literal("").transform(() => undefined)])
  .optional();
export const sellerShareUtmSourceSchema = optionalTrimmedString(100);
export const salePriceSchema = z
  .string()
  .trim()
  .min(1)
  .refine((value) => {
    const price = Number(value);
    return Number.isFinite(price) && price > 0;
  }, "Price must be greater than zero");
export const saleNotesSchema = optionalTrimmedString(2000);
export const salePhotosSchema = z
  .array(z.string().min(1))
  .min(1, "At least one photo is required")
  .max(8, "Maximum 8 photos allowed");
export const listNameSchema = trimmedRequiredString(1, 100);
export const listDescriptionValueSchema = trimmedString(2000);
export const listDescriptionSchema = trimmedString(2000)
  .optional()
  .transform((value) => (value ? value : undefined));
export const listDescriptionUpdateSchema = trimmedString(2000).optional();
export const listIconSchema = optionalTrimmedString(100);
export const listItemNotesValueSchema = trimmedString(2000);
export const listItemNotesSchema = optionalListItemMetadataString(2000);
export const listItemNotesUpdateSchema = nullableListItemMetadataString(2000);
export const listItemPricePaidValueSchema = z
  .string()
  .trim()
  .refine((value) => {
    if (!value) return true;
    const price = Number(value);
    return Number.isFinite(price) && price >= 0;
  }, "Price must be a valid number");
export const listItemPricePaidSchema = optionalListItemMetadataValue(listItemPricePaidValueSchema);
export const listItemPricePaidUpdateSchema = nullableListItemMetadataValue(
  listItemPricePaidValueSchema,
);
export const listItemPurchaseDateValueSchema = trimmedString(50);
export const listItemPurchaseDateSchema = optionalListItemMetadataString(50);
export const listItemPurchaseDateUpdateSchema = nullableListItemMetadataString(50);
export const listItemPurchasedFromValueSchema = trimmedString(200);
export const listItemPurchasedFromSchema = optionalListItemMetadataString(200);
export const listItemPurchasedFromUpdateSchema = nullableListItemMetadataString(200);
export const discogsImportUrlSchema = z.string().url();
export const contactNameSchema = createContactNameSchema();
export const contactEmailSchema = createContactEmailSchema();
export const contactMessageSchema = createContactMessageSchema();
export const commentContentSchema = trimmedRequiredString(1, 1000);

export const profileInputSchema = z
  .object({
    username: usernameSchema.optional(),
    displayName: profileDisplayNameSchema.optional(),
    bio: profileBioSchema,
    country: countryCodeSchema,
    city: profileCitySchema,
  })
  .strict();

export const onboardingProfileInputSchema = z
  .object({
    username: usernameSchema,
    displayName: profileDisplayNameSchema,
    bio: profileBioSchema,
    country: countryCodeSchema,
  })
  .strict();

export const onboardingVisibilityInputSchema = z
  .object({
    collectionPublic: z.boolean(),
    wishlistPublic: z.boolean(),
  })
  .strict();

export const sellerProfileInputSchema = z
  .object({
    email: sellerEmailSchema,
    country: countryCodeSchema,
    isSeller: z.boolean().optional(),
    shareUtmSource: sellerShareUtmSourceSchema,
    shareHideListings: z.boolean().optional(),
  })
  .superRefine((value, ctx) => {
    if (value.isSeller === false) {
      return;
    }

    if (!value.country) {
      ctx.addIssue({
        code: "custom",
        path: ["country"],
        message: "Country is required for seller profiles",
      });
    }
  });

export const marketplaceSaleMetadataSchema = z.object({
  price: salePriceSchema,
  currency: currencyCodeSchema,
  notes: saleNotesSchema,
  photos: salePhotosSchema,
  recordGrade: vinylGradeSchema.optional(),
  sleeveGrade: vinylGradeSchema.optional(),
});

export const listFormSchema = z.object({
  name: listNameSchema,
  description: listDescriptionSchema,
  icon: listIconSchema,
});

export const listUpdateFormSchema = z.object({
  name: listNameSchema.optional(),
  description: listDescriptionUpdateSchema,
  icon: listIconSchema,
  isPublic: z.boolean().optional(),
});

export const listItemMetadataSchema = z.object({
  recordGrade: vinylGradeSchema.optional(),
  sleeveGrade: vinylGradeSchema.optional(),
  notes: listItemNotesSchema,
  pricePaid: listItemPricePaidSchema,
  purchaseDate: listItemPurchaseDateSchema,
  purchasedFrom: listItemPurchasedFromSchema,
});

export const discogsImportInputSchema = z.object({
  url: discogsImportUrlSchema,
  destinationListId: z.string().min(1),
});

export const sortRuleStepSchema = z.object({
  field: z.enum([
    "artistName",
    "title",
    "originalYear",
    "artistCountry",
    "format",
    "addedAt",
    "releaseType",
    "recordGrade",
    "date",
    "offerPrice",
    "offerDropPercent",
  ]),
  direction: z.enum(["asc", "desc"]),
  buckets: z.array(z.string()).optional(),
});

export const sortRuleSchema = z.array(sortRuleStepSchema);

export const viewPreferenceSchema = z.object({
  viewMode: z.enum(["grid", "browse"]),
  browseDimension: z.enum(["country", "artist", "year", "genre"]).optional(),
});

export const sortPresetInputSchema = z.object({
  name: trimmedRequiredString(1, 100),
  rule: sortRuleSchema,
});

export const contactInputSchema = createContactInputSchema();

export const postContentSchema = z.string().trim().max(2000);

export const createPostInputSchema = z
  .object({
    content: postContentSchema.nullish(),
    images: z.array(z.string().url()).max(4).default([]),
    linkedEntityType: z.enum(["release", "work", "artist"]).nullish(),
    linkedEntityId: z.string().nullish(),
    linkedEntityName: z.string().nullish(),
    linkedEntityThumbUrl: z.string().nullish(),
  })
  .superRefine((value, ctx) => {
    if (!value.content && value.images.length === 0) {
      ctx.addIssue({
        code: "custom",
        path: ["content"],
        message: "Post must have content or images",
      });
    }
  });

export const updatePostInputSchema = z.object({
  content: z.string().trim().min(1).max(2000),
});

export const commentInputSchema = z.object({
  content: commentContentSchema,
});

export interface ContactValidationMessages {
  nameRequired?: string;
  emailInvalid?: string;
  messageMin?: string;
  messageMax?: string;
}

export function createContactInputSchema(messages: ContactValidationMessages = {}) {
  return z.object({
    name: createContactNameSchema(messages),
    email: createContactEmailSchema(messages),
    message: createContactMessageSchema(messages),
    locale: localeSchema.default("en"),
  });
}

function createContactNameSchema(messages: ContactValidationMessages = {}) {
  return trimmedRequiredString(1, 200, messages.nameRequired);
}

function createContactEmailSchema(messages: ContactValidationMessages = {}) {
  const emailSchema = messages.emailInvalid
    ? z.string().trim().email(messages.emailInvalid)
    : z.string().trim().email();

  return emailSchema.max(320, messages.emailInvalid);
}

function createContactMessageSchema(messages: ContactValidationMessages = {}) {
  return trimmedRequiredString(10, 5000, messages.messageMin, messages.messageMax);
}

function optionalTrimmedString(maxLength: number) {
  return trimmedString(maxLength)
    .optional()
    .transform((value) => (value ? value : undefined));
}

function optionalListItemMetadataString(maxLength: number) {
  return optionalListItemMetadataValue(trimmedString(maxLength));
}

function nullableListItemMetadataString(maxLength: number) {
  return nullableListItemMetadataValue(trimmedString(maxLength));
}

function optionalListItemMetadataValue<TSchema extends z.ZodType<string>>(schema: TSchema) {
  return z
    .union([schema, z.null()])
    .optional()
    .transform((value) => (value ? value : undefined));
}

function nullableListItemMetadataValue<TSchema extends z.ZodType<string>>(schema: TSchema) {
  return z
    .union([schema, z.null()])
    .optional()
    .transform((value) => {
      if (value === undefined) return undefined;
      return value ? value : null;
    });
}

function trimmedString(maxLength: number, message?: string) {
  return z.string().trim().max(maxLength, message);
}

function trimmedRequiredString(
  minLength: number,
  maxLength: number,
  minMessage?: string,
  maxMessage?: string,
) {
  return trimmedString(maxLength, maxMessage).min(minLength, minMessage);
}
